import React from "react";
import { FeatherAlertCircle } from "@subframe/core";
import { Badge } from "../../ui";
import { VendorCustomerPaymentRefundCategoryDetails } from "../../services/api";

interface VendorCustomerPaymentRefundViewContentProps {
  details: VendorCustomerPaymentRefundCategoryDetails | null;
  isLoading: boolean;
  currencySymbol: string;
}

const VendorCustomerPaymentRefundViewContent: React.FC<
  VendorCustomerPaymentRefundViewContentProps
> = ({ details, isLoading, currencySymbol }) => {
  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-10 text-body text-subtext-color">
        Loading customer payment refund details...
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-10 text-center text-body text-subtext-color">
        <FeatherAlertCircle className="text-neutral-400" />
        <span>Customer payment refund details are not available.</span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-2">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Customer Payment Refund
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {details.customer_name && (
              <Badge className="border-success-700 bg-success-700 text-white [&>span]:text-white">
                Customer: {details.customer_name}
              </Badge>
            )}
            {details.from_account_name && (
              <Badge className="border-neutral-700 bg-neutral-700 text-white [&>span]:text-white">
                From Account: {details.from_account_name}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col text-right">
          <span className="text-caption text-subtext-color">Refund Date</span>
          <span className="text-body-bold text-default-font">
            {details.date ? new Date(details.date).toLocaleDateString() : "—"}
          </span>
        </div>
      </div>

      {!details.payment ? (
        <div className="flex w-full flex-col items-center gap-2 rounded border border-dashed border-neutral-border px-4 py-6 text-center text-body text-subtext-color">
          <FeatherAlertCircle className="text-neutral-400" />
          <span>No customer payment was linked to this refund.</span>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-3 rounded border border-solid border-neutral-border bg-white px-6 py-4">
          <div className="flex items-center justify-between border-b border-neutral-border pb-3">
            <span className="text-body-bold text-subtext-color">
              Payment Details
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <span className="text-caption text-subtext-color">Payment #</span>
              <span className="text-body-bold text-default-font">
                {details.payment.payment_number || details.payment.payment_id}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-caption text-subtext-color">
                Amount Refunded
              </span>
              <span className="text-heading-4 font-heading-4 text-success-600">
                {currencySymbol}{" "}
                {details.payment.amount_refunded.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
          <span className="text-caption text-subtext-color">Payment Mode</span>
          <span className="text-body-bold text-default-font">
            {details.payment_mode || "—"}
          </span>
        </div>
        <div className="flex flex-col rounded-md border border-dashed border-neutral-border px-4 py-3">
          <span className="text-caption text-subtext-color">Reference #</span>
          <span className="text-body-bold text-default-font">
            {details.reference_number || "—"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default VendorCustomerPaymentRefundViewContent;
